import { createTheme, alpha } from '@mui/material/styles';

// Design System Colors (from design.md - "Warm Modernist" / "Quiet Luxury")
export const designTokens = {
  colors: {
    primary: {
      main: '#C5A059',      // Brushed Gold (CTAs, accents, verified badges)
      light: '#D4B373',
      dark: '#A88542',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#3C3E3F',      // Silhouette (text, headers, secondary CTAs)
      light: '#5A5C5D',
      dark: '#2A2B2C',
      contrastText: '#F0EDE9',
    },
    background: {
      default: '#F0EDE9',   // Cloud Dancer (main bg - "Warm White")
      paper: '#FFFFFF',
      subtle: '#E8E5E1',
    },
    success: {
      main: '#8A9A5B',      // Sage Moss (available status, success messages)
      light: '#A3B376',
      dark: '#6F7B48',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#E8A959',      // Amber (reserved status)
      light: '#F0C078',
      dark: '#D18F3A',
    },
    info: {
      main: '#7B9AB8',      // Soft blue for info messages
      light: '#9AB3CC',
      dark: '#5C7A95',
    },
    error: {
      main: '#C85A54',      // Muted red
      light: '#D97A75',
      dark: '#A8413C',
    },
    text: {
      primary: '#3C3E3F',
      secondary: alpha('#3C3E3F', 0.7),
      disabled: alpha('#3C3E3F', 0.4),
    },
    divider: alpha('#3C3E3F', 0.12),
  },
  typography: {
    fontFamilySerif: '"Playfair Display", "Georgia", serif',
    fontFamilySans: '"Satoshi", "Helvetica", "Arial", sans-serif',
  },
  spacing: {
    unit: 8,
  },
  borderRadius: {
    small: 8,
    medium: 16,
    large: 24,
  },
  shadows: {
    subtle: '0 2px 8px rgba(60, 62, 63, 0.06)',
    card: '0 2px 12px rgba(60, 62, 63, 0.08)',
    cardHover: '0 8px 24px rgba(60, 62, 63, 0.12)',
    float: '0 16px 40px rgba(60, 62, 63, 0.15)',
    gold: '0 4px 12px rgba(197, 160, 89, 0.3)',
  },
};

// Create the custom theme
export const theme = createTheme({
  palette: {
    primary: designTokens.colors.primary,
    secondary: designTokens.colors.secondary,
    background: designTokens.colors.background,
    success: designTokens.colors.success,
    warning: designTokens.colors.warning,
    info: designTokens.colors.info,
    error: designTokens.colors.error,
    text: designTokens.colors.text,
    divider: designTokens.colors.divider,
  },
  typography: {
    fontFamily: designTokens.typography.fontFamilySans,
    // Hero Headlines
    h1: {
      fontFamily: designTokens.typography.fontFamilySerif,
      fontWeight: 700,
      fontSize: '3.5rem',      // 56px
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      '@media (max-width:900px)': {
        fontSize: '2.5rem',    // 40px on tablet
      },
      '@media (max-width:600px)': {
        fontSize: '2rem',      // 32px on mobile
      },
    },
    // Section Titles
    h2: {
      fontFamily: designTokens.typography.fontFamilySerif,
      fontWeight: 600,
      fontSize: '2.75rem',     // 44px
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      '@media (max-width:900px)': {
        fontSize: '2rem',      // 32px on tablet
      },
      '@media (max-width:600px)': {
        fontSize: '1.75rem',   // 28px on mobile
      },
    },
    // Subsection Headers
    h3: {
      fontFamily: designTokens.typography.fontFamilySerif,
      fontWeight: 600,
      fontSize: '2rem',        // 32px
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1.5rem',    // 24px on mobile
      },
    },
    // Card Titles
    h4: {
      fontFamily: designTokens.typography.fontFamilySerif,
      fontWeight: 500,
      fontSize: '1.5rem',      // 24px
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1.25rem',   // 20px on mobile
      },
    },
    // Component Headers
    h5: {
      fontFamily: designTokens.typography.fontFamilySans,
      fontWeight: 600,
      fontSize: '1.25rem',     // 20px
      lineHeight: 1.5,
      '@media (max-width:600px)': {
        fontSize: '1.125rem',  // 18px on mobile
      },
    },
    h6: {
      fontFamily: designTokens.typography.fontFamilySans,
      fontWeight: 600,
      fontSize: '1.125rem',    // 18px
      lineHeight: 1.5,
    },
    // Body Text
    body1: {
      fontSize: '1rem',        // 16px
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '0.875rem',    // 14px
      lineHeight: 1.5,
    },
    // Captions & Small Text
    caption: {
      fontSize: '0.75rem',     // 12px
      lineHeight: 1.4,
      letterSpacing: '0.01em',
      color: alpha('#3C3E3F', 0.6),
    },
    // Buttons
    button: {
      fontWeight: 500,
      letterSpacing: '0.02em',
      textTransform: 'none',   // IMPORTANT: No UPPERCASE buttons
    },
  },
  shape: {
    borderRadius: designTokens.borderRadius.medium,
  },
  shadows: [
    'none',
    designTokens.shadows.subtle,
    designTokens.shadows.card,
    '0 4px 16px rgba(60, 62, 63, 0.10)',
    designTokens.shadows.cardHover,
    '0 12px 32px rgba(60, 62, 63, 0.14)',
    designTokens.shadows.float,
    '0 20px 48px rgba(60, 62, 63, 0.18)',
    '0 24px 56px rgba(60, 62, 63, 0.20)',
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 4px 8px rgba(0,0,0,0.1)',
    '0 8px 16px rgba(0,0,0,0.1)',
    '0 12px 24px rgba(0,0,0,0.1)',
    '0 16px 32px rgba(0,0,0,0.1)',
    '0 20px 40px rgba(0,0,0,0.1)',
    '0 24px 48px rgba(0,0,0,0.1)',
    '0 28px 56px rgba(0,0,0,0.1)',
    '0 32px 64px rgba(0,0,0,0.1)',
    '0 36px 72px rgba(0,0,0,0.1)',
    '0 40px 80px rgba(0,0,0,0.1)',
    '0 44px 88px rgba(0,0,0,0.1)',
    '0 48px 96px rgba(0,0,0,0.1)',
    '0 52px 104px rgba(0,0,0,0.1)',
    '0 56px 112px rgba(0,0,0,0.1)',
    '0 60px 120px rgba(0,0,0,0.1)',
  ] as any,
  components: {
    // Button Component Overrides
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.small,
          padding: '12px 32px',
          fontSize: '1rem',
          fontWeight: 500,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (max-width:600px)': {
            padding: '10px 24px',
            fontSize: '0.9375rem',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: designTokens.shadows.gold,
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${designTokens.colors.primary.main} 0%, ${designTokens.colors.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${designTokens.colors.primary.light} 0%, ${designTokens.colors.primary.main} 100%)`,
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: alpha(designTokens.colors.secondary.main, 0.04),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(designTokens.colors.primary.main, 0.08),
          },
        },
        sizeLarge: {
          padding: '14px 40px',
          fontSize: '1.125rem',
          minHeight: '48px',
          '@media (max-width:600px)': {
            padding: '12px 32px',
            minHeight: '44px', // Thumb-friendly on mobile
          },
        },
        sizeSmall: {
          padding: '8px 20px',
          fontSize: '0.875rem',
        },
      },
    },
    // Card Component Overrides
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.large,
          boxShadow: designTokens.shadows.card,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: designTokens.shadows.cardHover,
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    // TextField Component Overrides
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designTokens.borderRadius.small,
            backgroundColor: '#FFFFFF',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#FAFAF9',
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
                borderColor: designTokens.colors.primary.main,
              },
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: designTokens.colors.primary.main,
          },
        },
      },
    },
    // Chip Component Overrides
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.small,
          fontWeight: 500,
          fontSize: '0.875rem',
        },
        colorPrimary: {
          backgroundColor: designTokens.colors.primary.main,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: designTokens.colors.primary.dark,
          },
        },
        colorSuccess: {
          backgroundColor: designTokens.colors.success.main,
          color: '#FFFFFF',
        },
      },
    },
    // Paper Component Overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default MUI gradient
        },
        rounded: {
          borderRadius: designTokens.borderRadius.medium,
        },
        elevation1: {
          boxShadow: designTokens.shadows.subtle,
        },
        elevation2: {
          boxShadow: designTokens.shadows.card,
        },
        elevation4: {
          boxShadow: designTokens.shadows.cardHover,
        },
      },
    },
    // AppBar Component Overrides
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: designTokens.shadows.subtle,
          backgroundColor: '#FFFFFF',
          color: designTokens.colors.text.primary,
        },
      },
    },
    // Drawer Component Overrides
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${alpha(designTokens.colors.text.primary, 0.08)}`,
        },
      },
    },
    // Divider Component Overrides
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha(designTokens.colors.text.primary, 0.08),
        },
      },
    },
    // IconButton Component Overrides
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(designTokens.colors.primary.main, 0.08),
            transform: 'scale(1.05)',
          },
        },
      },
    },
    // List Item Button Overrides
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.small,
          marginBottom: '4px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(designTokens.colors.primary.main, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(designTokens.colors.primary.main, 0.12),
            color: designTokens.colors.primary.dark,
            '&:hover': {
              backgroundColor: alpha(designTokens.colors.primary.main, 0.16),
            },
          },
        },
      },
    },
    // Tab Component Overrides
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '1rem',
          minHeight: '48px',
          '&.Mui-selected': {
            color: designTokens.colors.primary.main,
          },
        },
      },
    },
    // Tabs Indicator
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: designTokens.colors.primary.main,
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    // Alert Component Overrides
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.medium,
        },
        standardSuccess: {
          backgroundColor: alpha(designTokens.colors.success.main, 0.12),
          color: designTokens.colors.success.dark,
        },
        standardWarning: {
          backgroundColor: alpha(designTokens.colors.warning.main, 0.12),
          color: designTokens.colors.warning.dark,
        },
      },
    },
    // Dialog Overrides
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: designTokens.borderRadius.large,
          boxShadow: designTokens.shadows.float,
        },
      },
    },
    // Backdrop for Modals
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#3C3E3F', 0.6),
          backdropFilter: 'blur(4px)',
        },
      },
    },
  },
});

export default theme;
